import { useTranslation } from "react-i18next";
import LogRocket from "logrocket";

export const CLOVER_APP_ID = "15X3CSAPQXJ7J";

// Item SKU for charing pay at pickup orders on square through barcode.
// Must be a 5 digit number that exists in the square item catalog.
export const PAY_AT_PICKUP_SKU = "99999";

export const WS_URL = import.meta.env.VITE_WS_URL;

export const API_URL = import.meta.env.VITE_API_URL;

// credentialsForUrl returns "include" when the given URL targets the same
// host as API_URL (so cookies are sent cross-origin to our own API), and
// undefined otherwise. Relative URLs are resolved against the current
// document's origin. Use this for helper functions that accept an opaque
// URL string so we don't leak cookies to third-party hosts.
export const credentialsForUrl = (url: string): RequestCredentials => {
  try {
    const apiHost = new URL(API_URL, window.location.origin).host;
    const targetHost = new URL(url, window.location.origin).host;
    return targetHost === apiHost ? "include" : undefined;
  } catch {
    return undefined;
  }
};

// Reconnection parameters for websocket clients. Using exponential backoff
// with random jitter avoids the "thundering herd" problem where many clients
// disconnect at once (e.g. backend restart) and then all retry simultaneously.
const WS_RECONNECT_BASE_DELAY = 1_000; // 1 second
const WS_RECONNECT_MAX_DELAY = 10_000; // 10 seconds
const WS_RECONNECT_JITTER_FACTOR = 0.3; // 30% jitter

/**
 * Calculate reconnection delay with exponential backoff and random jitter.
 * @param attempt 0-indexed retry attempt number
 * @returns delay in milliseconds
 */
export const getWebsocketReconnectDelay = (attempt: number): number => {
  // Exponential backoff: delay = baseDelay * 2^attempt
  const exponentialDelay = WS_RECONNECT_BASE_DELAY * Math.pow(2, attempt);
  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, WS_RECONNECT_MAX_DELAY);
  // Add symmetric jitter: randomize by ±JITTER_FACTOR
  const jitter = cappedDelay * WS_RECONNECT_JITTER_FACTOR * (Math.random() * 2 - 1);
  return Math.floor(cappedDelay + jitter);
};

export const STATUS = {
  UNDEFINED: undefined,
  PENDING: 0,
  STARTED: 1,
  DONE: 2,
} as const;

// DEPRECATED: Use i18n translations instead with keys: "not_started", "in_progress", "completed"
export const STATUS_DISPLAY: Record<number, string> = {
  0: "未開始",
  1: "進行中",
  2: "完成",
};

export const COLORS = [
  "#004FAC", // blue
  "rgba(0, 0, 0, 0.85)", // default text color
  "#FA824C", // orange
  "#358600", // green
  "#999B08", // yellow-green
];

export interface OrderTypeDisplay {
  color: string;
  logo?: string;
}

// Keys are lowercase
export const ORDER_TYPE_COLORS: Record<string, OrderTypeDisplay> = {
  "to go": { color: "#CC0029" },
  togo: { color: "#CC0029" },
  here: { color: "#b536d1" },
  "phone order": { color: "#FA824C" },
  phone: { color: "#FA824C" },
  stripe: { color: "#358600" },
  square: { color: "#3c4ab5" },
  doordash: { color: "#0A0B0B" },
  "door dash": { color: "#0A0B0B" },
  ubereats: { color: "#37DB4A" },
  "uber eats": { color: "#37DB4A" },
  grubhub: { color: "#FF3008" },
};

// Delivery service keywords for substring matching
const DELIVERY_SERVICE_KEYS = ["doordash", "door dash", "ubereats", "uber eats", "grubhub"];

/**
 * Look up the display config for an order type.
 * Supports exact match and substring matching for delivery services
 * (e.g. "DoorDash - Delivery" will match the "doordash" config).
 */
export const getOrderTypeDisplay = (orderType: string): OrderTypeDisplay | undefined => {
  const lower = orderType.toLowerCase();

  // Exact match first
  if (ORDER_TYPE_COLORS[lower]) {
    return ORDER_TYPE_COLORS[lower];
  }

  // Substring match for delivery services
  for (const key of DELIVERY_SERVICE_KEYS) {
    if (lower.includes(key)) {
      return ORDER_TYPE_COLORS[key];
    }
  }

  return undefined;
};

export const DONE_ORDER_TYPE_COLORS: Record<string, string> = {
  online: "rgba(255, 220, 0, 0.5)", // Yellow for online orders
  doordash: "rgba(255, 61, 0, 0.5)", // DoorDash red
  ubereats: "rgba(6, 200, 66, 0.5)", // UberEats green
  grubhub: "rgba(255, 48, 8, 0.5)", // Grubhub orange
  in_store: "rgba(60, 74, 181, 0.5)", // Blue for in-store
  others: "rgba(250, 130, 76, 0.5)", // Orange for phone/others
};

export const STAR_PRIORITY = 10000;

// Wrapper over btoa to handle non-ascii characters
function toBase64UTF8(str) {
  // 1. Convert the Unicode string to a byte array (Uint8Array)
  const bytes = new TextEncoder().encode(str);

  // 2. Convert bytes to a "binary string" (one byte per char)
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join("");

  // 3. Base64 encode the binary string
  return btoa(binString);
}

export const getCredentials = (): string => {
  const username = sessionStorage.getItem("username");
  const password = sessionStorage.getItem("password");
  return toBase64UTF8(`${username}:${password}`);
};

/**
 * Get LogRocket session ID for tracking user sessions in production.
 * Returns null if LogRocket is not initialized or not in production.
 */
export const getLogRocketSessionId = (): string | null => {
  if (!import.meta.env.PROD) {
    return null;
  }
  try {
    // sessionID is a runtime property available after LogRocket is initialized
    // We use type assertion since it's not in the official TypeScript definitions
    const sessionId = (LogRocket as unknown as { sessionID?: string }).sessionID;
    return sessionId || null;
  } catch {
    return null;
  }
};

/**
 * Build headers with LogRocket session ID included.
 * This helps correlate backend logs with LogRocket sessions.
 */
export const buildHeadersWithSessionId = (
  additionalHeaders: Record<string, string> = {}
): Record<string, string> => {
  const sessionId = getLogRocketSessionId();
  const headers: Record<string, string> = { ...additionalHeaders };

  if (sessionId) {
    headers["X-LogRocket-Session-ID"] = sessionId;
  }

  return headers;
};

const logAndHandleError = (
  err: any,
  handleError?: ((err: any) => void) | null,
  suppressErrorLog?: boolean
): void => {
  if (!suppressErrorLog) {
    console.error(err);
  }
  if (handleError) {
    handleError(err);
  }
};

export const processError = (
  err: any,
  handleError?: ((err: any) => void) | null,
  suppressErrorLog?: boolean
): void => {
  if (err instanceof Error) {
    logAndHandleError(err, handleError, suppressErrorLog);
  } else {
    if (!suppressErrorLog) {
      console.log("error response headers", Object.fromEntries(err.headers.entries()));
    }
    err
      .text()
      .then((errorMessage: string) =>
        logAndHandleError(errorMessage, handleError, suppressErrorLog)
      );
  }
};

/** Thrown by fetchWebsocketTicket when the API answers with a non-2xx status. */
export class HttpStatusError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpStatusError";
    this.status = status;
  }
}

/**
 * Mints a short-lived, single-use ticket for authenticating a websocket
 * upgrade.
 *
 * The browser cannot put an Authorization header on a websocket handshake, so
 * we trade our HTTP Basic credentials for a ticket over a normal authenticated
 * request and then pass the ticket in the websocket URL.
 *
 * Throws HttpStatusError on a non-2xx response; a 404 means the API predates
 * the ticket endpoint (see useReconnectingWebSocket for the fallback).
 */
export const fetchWebsocketTicket = async (restaurantId: string): Promise<string> => {
  const url = `${API_URL}/v1/r/${restaurantId}/ws_ticket`;
  const res = await fetch(url, {
    credentials: credentialsForUrl(url),
    method: "POST",
    headers: buildHeadersWithSessionId({
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    }),
    body: "{}",
  });
  if (!res.ok) {
    throw new HttpStatusError(res.status, `failed to get websocket ticket: ${res.status}`);
  }
  const body = await res.json();
  return body.ticket;
};

export const getFetch = (
  url: string,
  handleResponse: (data: any) => void,
  handleError?: ((err: any) => void) | null,
  suppressErrorLog?: boolean
): void => {
  fetch(url, {
    credentials: credentialsForUrl(url),
    headers: buildHeadersWithSessionId({
      Authorization: `Basic ${getCredentials()}`,
    }),
  })
    .then((res) => {
      if (!res.ok) {
        throw res;
      }
      res.json().then((respJSON) => {
        handleResponse(respJSON);
      });
    })
    .catch((err) => {
      processError(err, handleError, suppressErrorLog);
    });
};

export const postFetch = (
  url: string,
  requestBody: any,
  handleResponse?: ((data: any, date?: string | null, etag?: string | null) => void) | null,
  handleError?: ((err: any) => void) | null,
  suppressErrorLog?: boolean
): void => {
  fetch(url, {
    credentials: credentialsForUrl(url),
    method: "POST",
    headers: buildHeadersWithSessionId({
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(requestBody),
  })
    .then((res) => {
      if (!res.ok) {
        throw res;
      }
      if (handleResponse) {
        res.json().then((respJSON) => {
          handleResponse(respJSON);
        });
      }
    })
    .catch((err) => {
      processError(err, handleError, suppressErrorLog);
    });
};

export const postFetchEmptyBody = (
  url: string,
  handleError?: ((err: any) => void) | null
): void => {
  postFetch(url, {}, null, handleError);
};

// Public API calls (no authentication required)
export const publicPostFetch = (
  url: string,
  requestBody: any,
  handleResponse?: ((data: any) => void) | null,
  handleError?: ((err: any) => void) | null
): void => {
  fetch(url, {
    credentials: credentialsForUrl(url),
    method: "POST",
    headers: buildHeadersWithSessionId({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(requestBody),
  })
    .then((res) => {
      if (!res.ok) {
        throw res;
      }
      if (handleResponse) {
        res.json().then((respJSON) => {
          handleResponse(respJSON);
        });
      }
    })
    .catch((err) => {
      processError(err, handleError);
    });
};

// Put items with no actions at the back of the array
// Otherwise, sort by item name, then item attributes ID, then item ID.
export const sortItems = (items: any[] | null | undefined): any[] | null | undefined => {
  if (!items) {
    return items;
  }

  const newItems = [...items];
  newItems.sort((item1: any, item2: any) => {
    if (!item1.actions && item2.actions) {
      return 1;
    }
    if (item1.actions && !item2.actions) {
      return -1;
    }

    let key1 = item1.attributes.alternate_name;
    let key2 = item2.attributes.alternate_name;
    if (key1 !== key2) {
      return key1.localeCompare(key2);
    }

    key1 = item1.attributes.name;
    key2 = item2.attributes.name;
    if (key1 !== key2) {
      return key1.localeCompare(key2);
    }

    key1 = item1.attributes_id;
    key2 = item2.attributes_id;
    if (key1 !== key2) {
      return key1.localeCompare(key2);
    }

    return item1.id.localeCompare(item2.id);
  });
  return newItems;
};

export const getOrderState = (order: any): "done" | "started" | "pending" | "canceled" => {
  if (order.done_time) {
    return "done";
  } else if (order.start_time) {
    return "started";
  } else if (order.cancel_time) {
    return "canceled";
  } else {
    return "pending";
  }
};

export const hasNewOrders = (oldOrders: any[], newOrders: any[]): boolean => {
  const oldOrderSet = new Set<string>();
  oldOrders.forEach((order) => oldOrderSet.add(order.id));
  for (const newOrder of newOrders) {
    if (!oldOrderSet.has(newOrder.id)) {
      return true;
    }
  }
  return false;
};

export const capitalizeFirstLetter = (s: string): string =>
  `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

// attrs: ItemAttributes
export const itemName = (attrs: any): string => {
  const { t, i18n } = useTranslation();
  if (attrs.intl_name) {
    const prefix = attrs.pos_key ? "#" + attrs.pos_key : "";

    // Iterate through languages in fallback order.
    for (const lang of i18n.languages) {
      if (lang in attrs.intl_name && attrs.intl_name[lang]) {
        return prefix + " " + attrs.intl_name[lang];
      }
    }
  }

  // Fallback to old behavior.
  return attrs.alternate_name ? attrs.alternate_name : attrs.name;
};

// Translate workstation names based on known Chinese names
export const getWorkstationTranslationKey = (workstationName: string): string => {
  const nameMap: { [key: string]: string } = {
    炸: "workstation_fry",
    烤: "workstation_grill",
    打飯: "workstation_rice",
    組裝: "workstation_assembly",
    抓料: "workstation_prep",
  };

  return nameMap[workstationName] || workstationName;
};
